import { useQuery } from "@tanstack/react-query";

import {
  getMailCapabilities,
  getMailSendingSettings,
  mailCapabilitiesQueryKey,
  tenantMailSendingSettingsQueryKey,
  type MailCapabilities,
  type MailSenderStatus,
  type MailSendingSettings,
} from "../../api/mail-api.js";
import { useAuthStore } from "../../auth/auth-store.js";
import { useAllMailSenderPages } from "./useAllMailSenderPages.js";

/**
 * Whether workspace email is going out right now (PB-5411, PB-5435).
 *
 * `unknown` is a real answer rather than a placeholder: the server inventory
 * is still loading, still paging, or failed, or one of the reads that follow it
 * did. Callers must render wording that claims neither a delivery nor a pause
 * for it, because the thing a credential dialog must never do is withhold the
 * link while it waits on an advisory fact.
 */
export type MailSendingState = "on" | "paused" | "unknown";

/** The ACTIVE sender in an operational inventory, or null when there is none. */
export function activeSender<T extends { readonly status: MailSenderStatus }>(
  rows: readonly T[],
): T | null {
  return rows.find((row) => row.status === "active") ?? null;
}

/**
 * True once every page of the operational inventory has arrived, so "there is
 * no active server" is knowable rather than merely not yet seen: an active
 * server can sit on a later page.
 *
 * `isSuccess`, not `!isLoading`. In this TanStack version `isLoading` is
 * `isPending && isFetching`, so a first read that is PAUSED rather than in
 * flight — the browser is offline, which is the default network mode's
 * behaviour — reports `isLoading: false` with no data at all. `hasNextPage` is
 * then undefined and `isFetchingNextPage` false, so every guard here passed and
 * an inventory that was never read became a settled "there is no active
 * server". The page would say sending is paused on the strength of nothing.
 * `isSuccess` is the only one of these that means data actually arrived, and it
 * subsumes the error guard — including a REFETCH that fails, which keeps the
 * old pages in `data` while the query reports `status: "error"`.
 */
export function isInventorySettled(inventory: {
  readonly isSuccess: boolean;
  readonly hasNextPage: boolean;
  readonly isFetchingNextPage: boolean;
}): boolean {
  return (
    inventory.isSuccess &&
    !inventory.hasNextPage &&
    !inventory.isFetchingNextPage
  );
}

/**
 * What an admin surface may honestly CLAIM about sending, as opposed to the
 * flag it read. The ONE answer the Mail server page and both credential dialogs
 * render (PB-5435).
 *
 * `sendingEnabled` is the stored or derived settings value. It goes stale the
 * moment the last active server is turned off: the row keeps saying "on" while
 * nothing can leave. The Mail server page used to contradict itself on one
 * screen for exactly this reason — "Active server: None" beside a pill reading
 * "Sending on" — and the credential dialogs, which read only that flag, went on
 * saying "We've queued an email" after the page was fixed (PB-5411 → PB-5435).
 *
 * Three values rather than a boolean, because "not known yet" is not "no" — a
 * boolean forced each reader to pick a side, and they picked the flattering
 * one: a healthy "Email and in-app" beside a header reading "Checking...".
 *
 * Order matters. A settled inventory with no active server is CERTAIN that
 * nothing is going out, whatever the settings read is doing, so it answers
 * first. A workspace with no sending control has no pause to report, so once a
 * server is confirmed the answer is "on" — there is no setting to consult.
 *
 * `isError` is checked BESIDE the data, not instead of it. A refetch that fails
 * keeps the last successful value in `data` while setting `isError`, so reading
 * `data` alone would let a stale "paused" outlive the read that established it.
 */
export function deriveEmailReach(input: {
  readonly activeServerKnown: boolean;
  readonly hasActiveServer: boolean;
  readonly capabilities: {
    readonly isError: boolean;
    readonly data: MailCapabilities | undefined;
  };
  readonly settings: {
    readonly isError: boolean;
    readonly data: MailSendingSettings | undefined;
  };
}): MailSendingState {
  const { activeServerKnown, hasActiveServer, capabilities, settings } = input;
  if (!activeServerKnown) return "unknown";
  if (!hasActiveServer) return "paused";
  if (capabilities.isError || capabilities.data === undefined) return "unknown";
  if (capabilities.data.sendingControl.enabled !== true) return "on";
  if (settings.isError || settings.data === undefined) return "unknown";
  return settings.data.sendingEnabled ? "on" : "paused";
}

/**
 * `deriveEmailReach` for surfaces outside the Mail server page — today the
 * invite and set-password dialogs, which say "we queued an email" only when it
 * is true. The invitation enqueues an outbox row inside its own transaction and
 * dispatch is asynchronous, so even "on" means queued, never sent.
 *
 * Every read here uses the key, page shape and `enabled` condition the Mail
 * server page uses, so the two share one cache entry per read instead of
 * issuing them twice, and cannot answer the same question two ways. The
 * tenant's own reads are keyed by tenant, so a session handover to another
 * tenant starts from "unknown" rather than from the previous tenant's answer.
 */
export function useMailSendingState(): MailSendingState {
  const tenantId = useAuthStore((state) => state.claims?.tenantId ?? null);
  const operational = useAllMailSenderPages("operational");
  const capabilities = useQuery({
    queryKey: mailCapabilitiesQueryKey,
    queryFn: getMailCapabilities,
  });
  const settings = useQuery({
    queryKey: tenantMailSendingSettingsQueryKey(tenantId),
    queryFn: getMailSendingSettings,
    enabled: capabilities.data?.sendingControl.enabled === true,
  });
  return deriveEmailReach({
    activeServerKnown: isInventorySettled(operational),
    hasActiveServer: activeSender(operational.rows) !== null,
    capabilities,
    settings,
  });
}
